import { NumberType } from "../../core/formatters/number.formatter"
import { pieChartTransform } from "../adapters/transformers/pie-chart.transform"
import { PieChartService } from "../services/pie-chart.service"

export enum PieChartType {
    JURISDICTION_MARKET_CAPS = "jurisdiction-market-caps",
    DOMICILIATION_MARKET_CAPS = "domiciliation-market-caps",
    STABLECOIN_MARKET_VALUE_PER_PEGGED_ASSET = "stablecoin-market-value-per-pegged-asset",
    BTC_HASHRATE_BY_COUNTRY = "btc_hashrate_by_country",
    BTC_MINING_ENERGY_SOURCE = "btc_mining_energy_source",
    ETFS_IBIT_SP500_GOLD_AUM = "etfs_ibit_sp500_gold_aum",
    ETP_CRYPTO_EUROPE_AUM = "etp_crypto_europe_aum",
    TBILLS_HOLDERS = "tbills_holders",
}

export interface PieChartConfig {
    id: string
    dataLoader: () => Promise<any>
    transformer: (data: any) => any
    options?: {
        colors?: string[]
        innerRadius?: number
        outerRadius?: number
        labelThreshold?: number
        showPercentage?: boolean
        showAbsoluteValue?: boolean
        sortByValue?: boolean
        absoluteValueFormat?: NumberType
    }
}

export const defaultPieOptions: Required<NonNullable<PieChartConfig['options']>> = {
    colors: undefined as unknown as string[],
    innerRadius: 0.4,
    outerRadius: 1,
    labelThreshold: 3,
    showPercentage: true,
    showAbsoluteValue: true,
    sortByValue: true,
    absoluteValueFormat: NumberType.price
}

export const createPieChartConfigs = (pieChartService: PieChartService) => {
    return new Map<string, PieChartConfig>([
        [PieChartType.JURISDICTION_MARKET_CAPS, {
            id: PieChartType.JURISDICTION_MARKET_CAPS,
            dataLoader: () => pieChartService.loadStablecoinsLatest(),
            transformer: pieChartTransform.jurisdictionMarketCapsToPieDO,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.ETFS_IBIT_SP500_GOLD_AUM, {
            id: PieChartType.ETFS_IBIT_SP500_GOLD_AUM,
            dataLoader: () => pieChartService.loadV2(PieChartType.ETFS_IBIT_SP500_GOLD_AUM),
            transformer: pieChartTransform.defaultPieChartTransform,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.ETP_CRYPTO_EUROPE_AUM, {
            id: PieChartType.ETP_CRYPTO_EUROPE_AUM,
            dataLoader: () => pieChartService.loadV2(PieChartType.ETP_CRYPTO_EUROPE_AUM),
            transformer: pieChartTransform.defaultPieChartTransform,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.TBILLS_HOLDERS, {
            id: PieChartType.TBILLS_HOLDERS,
            dataLoader: () => pieChartService.loadV2(PieChartType.TBILLS_HOLDERS),
            transformer: pieChartTransform.defaultPieChartTransform,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.DOMICILIATION_MARKET_CAPS, {
            id: PieChartType.DOMICILIATION_MARKET_CAPS,
            dataLoader: () => pieChartService.loadStablecoinsLatest(),
            transformer: pieChartTransform.domiciliationMarketCapsToPieDO,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.STABLECOIN_MARKET_VALUE_PER_PEGGED_ASSET, {
            id: PieChartType.STABLECOIN_MARKET_VALUE_PER_PEGGED_ASSET,
            dataLoader: () => pieChartService.loadStablecoinsLatest(),
            transformer: pieChartTransform.stablecoinMarketValuePerPeggedAssetToPieDO,
            options: { ...defaultPieOptions }
        }],
        [PieChartType.BTC_HASHRATE_BY_COUNTRY, {
            id: PieChartType.BTC_HASHRATE_BY_COUNTRY,
            dataLoader: () => pieChartService.loadV2(PieChartType.BTC_HASHRATE_BY_COUNTRY),
            transformer: pieChartTransform.defaultPieChartTransform,
            options: { ...defaultPieOptions, showAbsoluteValue: false }
        }],
        [PieChartType.BTC_MINING_ENERGY_SOURCE, {
            id: PieChartType.BTC_MINING_ENERGY_SOURCE,
            dataLoader: () => pieChartService.loadV2(PieChartType.BTC_MINING_ENERGY_SOURCE),
            transformer: pieChartTransform.defaultPieChartTransform,
            options: { ...defaultPieOptions, showAbsoluteValue: false }
        }],
    ]);
};

// Legacy support - keeping the old interface for backward compatibility
export const pieTypesOptions = new Map<string, Function>([
    [PieChartType.JURISDICTION_MARKET_CAPS, pieChartTransform.jurisdictionMarketCapsToPieDO]
]) 